import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';

import {
  About,
  Contact,
  Credentials,
  Experience,
  Hero,
  Process,
  Projects,
  Services,
  Sidebar,
  Skills,
  SystemMap,
  WhyWorkWithMe,
} from '../../components/Portfolio';
import { ThemeContext } from '../../contexts/ThemeContext';
import {
  about,
  credentials,
  developmentProcess,
  experience,
  featuredProjects,
  metrics,
  profile,
  projectGroups,
  serviceOfferings,
  skillGroups,
  whyWorkWithMe,
} from '../../data/portfolioData';
import './Portfolio.css';

const navItems = [
  { id: 'home', index: '01', label: 'Home', href: '#home' },
  { id: 'services', index: '02', label: 'Services', href: '#services' },
  { id: 'projects', index: '03', label: 'Projects', href: '#projects' },
  { id: 'process', index: '04', label: 'Process', href: '#process' },
  { id: 'about', index: '05', label: 'About', href: '#about' },
  { id: 'contact', index: '06', label: 'Contact', href: '#contact' },
];

function Main() {
  const { isDark, toggleTheme } = useContext(ThemeContext);
  const [activeSection, setActiveSection] = useState('home');
  const shellRef = useRef(null);

  const sectionIds = useMemo(() => navItems.map((item) => item.id), []);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return undefined;

    const canAnimate = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
    if (!canAnimate) return undefined;

    const handlePointerMove = (event) => {
      const x = event.clientX / window.innerWidth - 0.5;
      const y = event.clientY / window.innerHeight - 0.5;
      shell.style.setProperty('--mx', x.toFixed(4));
      shell.style.setProperty('--my', y.toFixed(4));
      shell.style.setProperty('--cursor-x', `${event.clientX}px`);
      shell.style.setProperty('--cursor-y', `${event.clientY}px`);
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    return () => window.removeEventListener('pointermove', handlePointerMove);
  }, []);

  useEffect(() => {
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible?.target?.id) setActiveSection(visible.target.id);
      },
      { rootMargin: '-20% 0px -55% 0px', threshold: [0.18, 0.35, 0.55] }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [sectionIds]);

  useEffect(() => {
    const canAnimate = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
    const revealItems = Array.from(document.querySelectorAll('.sectionReveal'));

    if (!canAnimate) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
    );

    revealItems.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={shellRef}
      className={`portfolioShell ${isDark ? 'portfolioShell--dark' : 'portfolioShell--light'}`}
    >
      <Helmet>
        <title>Full-Stack Web & Mobile App Developer | {profile.name}</title>
        <meta
          name="description"
          content="Arijit Das is a full-stack developer building custom web applications, mobile apps, React frontends, Node.js APIs, backend systems, and deployment-ready software."
        />
      </Helmet>

      <Sidebar
        profile={profile}
        navItems={navItems}
        activeSection={activeSection}
        isDark={isDark}
        toggleTheme={toggleTheme}
        onSectionSelect={setActiveSection}
      />

      <main className="portfolioMain">
        <Hero profile={profile} metrics={metrics} />
        <SystemMap />
        <Services services={serviceOfferings} />
        <Projects featuredProjects={featuredProjects} projectGroups={projectGroups} />
        <Process steps={developmentProcess} />
        <WhyWorkWithMe benefits={whyWorkWithMe} />
        <About about={about} />
        <Experience experience={experience} />
        <Skills skillGroups={skillGroups} />
        <Credentials credentials={credentials} />
        <Contact profile={profile} />
      </main>
    </div>
  );
}

export default Main;
